import {
  getCanonicalProvinceCodesSortedAlphabeticallyByDisplayName,
  provName,
  toProv,
} from "../../components/Analytics/PriceReports/provinceUtils";
import { insightsDesignEnabled } from "../../common/utils/insightsDesignEnabled";
import { brandRed, stone } from "./design/designTokens";
import {
  INSIGHTS_BAR_CHART_MAX_BARS,
  limitOrderedCategories,
  resolveInsightsBarChartColor,
} from "./pricingChartsUtils";

const PROVINCE_SNAPSHOT_BAR_MUTED_COLOR = insightsDesignEnabled()
  ? stone
  : "rgba(36, 87, 165, 0.45)";

export function getAvgFromBeefAiAggregateRow(row = {}) {
  return readBeefAiAggregateField(row, "avg", "unit_price");
}

export function getCountFromBeefAiAggregateRow(row = {}) {
  if (typeof row.count === "number") return row.count;
  if (typeof row.count === "string") return Number(row.count) || 0;
  if (row.count && typeof row.count === "object") {
    if (typeof row.count["*"] === "number") return row.count["*"];
    const firstValue = Object.values(row.count)[0];
    return Number(firstValue) || 0;
  }
  return Number(row?.count || 0);
}

function parseAggregateNumber(value) {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "" && !Number.isNaN(Number(value))) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function readBeefAiAggregateField(row, aggregateKey, fieldName = "unit_price") {
  const flatKey = `${aggregateKey}->${fieldName}`;
  const flatValue = parseAggregateNumber(row[flatKey]);
  if (flatValue != null) return flatValue;
  const nested = row?.[aggregateKey];
  const nestedValue = parseAggregateNumber(nested?.[fieldName]);
  if (nestedValue != null) return nestedValue;
  const snakeKey = `${aggregateKey}_${fieldName}`;
  const snakeValue = parseAggregateNumber(row[snakeKey]);
  if (snakeValue != null) return snakeValue;
  return null;
}

export function getMinFromBeefAiAggregateRow(row = {}) {
  return readBeefAiAggregateField(row, "min");
}

export function getMaxFromBeefAiAggregateRow(row = {}) {
  return readBeefAiAggregateField(row, "max");
}

export function getAvgNetWeightFromBeefAiAggregateRow(row = {}) {
  return readBeefAiAggregateField(row, "avg", "unit_quantity");
}

export function getMinNetWeightFromBeefAiAggregateRow(row = {}) {
  return readBeefAiAggregateField(row, "min", "unit_quantity");
}

export function getMaxNetWeightFromBeefAiAggregateRow(row = {}) {
  return readBeefAiAggregateField(row, "max", "unit_quantity");
}

export function getAvgTotalWeightFromBeefAiAggregateRow(row = {}) {
  return readBeefAiAggregateField(row, "avg", "raw_unit_quantity");
}

export function getMinTotalWeightFromBeefAiAggregateRow(row = {}) {
  return readBeefAiAggregateField(row, "min", "raw_unit_quantity");
}

export function getMaxTotalWeightFromBeefAiAggregateRow(row = {}) {
  return readBeefAiAggregateField(row, "max", "raw_unit_quantity");
}

/**
 * Chart A: one bar per retailer, sorted descending by average unit price.
 *
 * @param {object[]} retailerSnapshotRows
 * @returns {{ retailer: string, avgUnitPrice: number, priceHistoryRowCount: number }[]}
 */
export function buildRetailerSnapshotBars(retailerSnapshotRows) {
  const enriched = (retailerSnapshotRows || [])
    .map((row) => {
      const retailer = String(row.retailer ?? "").trim();
      if (!retailer) return null;
      return {
        retailer,
        avgUnitPrice: getAvgFromBeefAiAggregateRow(row),
        priceHistoryRowCount: getCountFromBeefAiAggregateRow(row),
      };
    })
    .filter(Boolean);

  return [...enriched].sort((a, b) => b.avgUnitPrice - a.avgUnitPrice);
}

function compareProvinceCodesAlphabeticallyByDisplayName(codeA, codeB) {
  return provName(codeA).localeCompare(provName(codeB));
}

/**
 * Canonical provinces with observations first (avg unit price descending), then
 * provinces without data (display name A–Z).
 *
 * @param {string[]} provinceCodes
 * @param {Record<string, number | undefined>} avgUnitPriceByProvinceCode
 * @returns {string[]}
 */
function sortProvinceCodesForSnapshotChart(provinceCodes, avgUnitPriceByProvinceCode) {
  const withData = [];
  const withoutData = [];

  for (const code of provinceCodes) {
    if (avgUnitPriceByProvinceCode[code] != null) {
      withData.push(code);
    } else {
      withoutData.push(code);
    }
  }

  withData.sort((codeA, codeB) => {
    const priceDelta =
      avgUnitPriceByProvinceCode[codeB] - avgUnitPriceByProvinceCode[codeA];
    if (priceDelta !== 0) return priceDelta;
    return compareProvinceCodesAlphabeticallyByDisplayName(codeA, codeB);
  });

  withoutData.sort(compareProvinceCodesAlphabeticallyByDisplayName);

  return [...withData, ...withoutData];
}

/**
 * Chart B: all canonical provinces; `null` in data where no observations.
 * Bar order: descending avg unit price, then provinces without data (A–Z).
 * Duplicate API rows for the same province code keep the row with the higher `count`.
 *
 * @param {object[]} provinceSnapshotRows
 */
export function buildProvinceAvgUnitPriceChart(provinceSnapshotRows) {
  const avgUnitPriceByProvinceCode = {};
  const countByProvinceCode = {};

  for (const row of provinceSnapshotRows || []) {
    const code = toProv(row.province);
    if (!code) continue;
    const nextCount = getCountFromBeefAiAggregateRow(row);
    const prevCount = countByProvinceCode[code] || 0;
    if (nextCount >= prevCount) {
      countByProvinceCode[code] = nextCount;
      avgUnitPriceByProvinceCode[code] = getAvgFromBeefAiAggregateRow(row);
    }
  }

  const canonicalProvinceCodes = getCanonicalProvinceCodesSortedAlphabeticallyByDisplayName();
  const orderedProvinceCodes = limitOrderedCategories(
    sortProvinceCodesForSnapshotChart(canonicalProvinceCodes, avgUnitPriceByProvinceCode),
    INSIGHTS_BAR_CHART_MAX_BARS
  );
  const labels = orderedProvinceCodes.map((code) => provName(code));
  const data = orderedProvinceCodes.map((code) => {
    const avg = avgUnitPriceByProvinceCode[code];
    return avg == null ? null : Number(avg.toFixed(2));
  });
  const provinceChartHasAnyData = orderedProvinceCodes.some(
    (code) => avgUnitPriceByProvinceCode[code] != null
  );

  return { labels, data, provinceCodes: orderedProvinceCodes, provinceChartHasAnyData };
}

/**
 * Bar colours for the province snapshot chart. Highlights the active province filter in brand red.
 * @param {string[]} provinceCodes
 * @param {string | null | undefined} selectedProvinceCode Canonical province code (e.g. AB).
 * @returns {string[]}
 */
export function buildProvinceSnapshotBarBackgroundColors(
  provinceCodes,
  selectedProvinceCode
) {
  const defaultColor = resolveInsightsBarChartColor();
  if (!selectedProvinceCode) {
    return (provinceCodes || []).map(() => defaultColor);
  }

  return (provinceCodes || []).map((code) =>
    code === selectedProvinceCode ? brandRed : PROVINCE_SNAPSHOT_BAR_MUTED_COLOR
  );
}

/**
 * @typedef {object} QuarterlyProvinceAggregateRow
 * @property {string} code
 * @property {string} displayName
 * @property {number} avgUnitPrice
 * @property {number} priceHistoryRowCount
 */

/**
 * @typedef {object} QuarterlyProvinceSummary
 * @property {string} code
 * @property {string} displayName
 * @property {number} avgUnitPrice
 */

/**
 * Merges duplicate province codes (higher `priceHistoryRowCount` wins, same `>=` rule as
 * {@link buildProvinceAvgUnitPriceChart}), then picks lowest and highest `avgUnitPrice`.
 * Ties on price break by `displayName` A–Z.
 *
 * @param {QuarterlyProvinceAggregateRow[]} aggregateRows
 * @returns {{ lowestProvince: QuarterlyProvinceSummary | null, highestProvince: QuarterlyProvinceSummary | null }}
 */
export function pickLowestAndHighestProvinceByAvgPrice(aggregateRows = []) {
  const mergedByCode = new Map();

  for (const row of aggregateRows || []) {
    const code = String(row?.code ?? "").trim();
    if (!code) continue;

    const nextCount = Number(row.priceHistoryRowCount) || 0;
    const existing = mergedByCode.get(code);
    if (!existing || nextCount >= existing.priceHistoryRowCount) {
      mergedByCode.set(code, {
        code,
        displayName: row.displayName,
        avgUnitPrice: row.avgUnitPrice,
        priceHistoryRowCount: nextCount,
      });
    }
  }

  const mergedProvinces = [...mergedByCode.values()];
  if (mergedProvinces.length === 0) {
    return { lowestProvince: null, highestProvince: null };
  }

  const pickExtreme = (direction) => {
    let chosen = mergedProvinces[0];
    for (let index = 1; index < mergedProvinces.length; index += 1) {
      const candidate = mergedProvinces[index];
      const priceDelta = candidate.avgUnitPrice - chosen.avgUnitPrice;
      if (direction === "min") {
        if (priceDelta < 0) {
          chosen = candidate;
        } else if (
          priceDelta === 0 &&
          candidate.displayName.localeCompare(chosen.displayName) < 0
        ) {
          chosen = candidate;
        }
      } else if (priceDelta > 0) {
        chosen = candidate;
      } else if (
        priceDelta === 0 &&
        candidate.displayName.localeCompare(chosen.displayName) < 0
      ) {
        chosen = candidate;
      }
    }
    return {
      code: chosen.code,
      displayName: chosen.displayName,
      avgUnitPrice: chosen.avgUnitPrice,
    };
  };

  return {
    lowestProvince: pickExtreme("min"),
    highestProvince: pickExtreme("max"),
  };
}
